import React, { useState } from "react";
import { playResult, playFanfare } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { Check, X, RotateCcw, Volume2 } from "lucide-react";
import { StaffViewer } from "@/components/StaffViewer";
import { Button } from "@/components/ui/button";
import { playPitch } from "@/lib/audio";

// Reusable quiz runner. props: questions[], quizId, onComplete(score,total)
export const QuizEngine = ({ questions, quizId, onComplete, title, passMark }) => {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const q = questions[idx];

  const choose = (opt) => {
    if (answered) return;
    setSelected(opt);
    setAnswered(true);
    if (opt === q.answer) setScore((s) => s + 1);
    playResult(opt === q.answer);
  };

  const next = () => {
    if (idx + 1 >= questions.length) {
      setDone(true);
      if (score === questions.length) playFanfare(0.1, title || "Lesson quiz");
      if (onComplete) onComplete(score, questions.length, !passMark || score >= passMark);
    } else {
      setIdx(idx + 1);
      setSelected(null);
      setAnswered(false);
    }
  };

  const restart = () => {
    setIdx(0);
    setSelected(null);
    setAnswered(false);
    setScore(0);
    setDone(false);
  };

  if (done) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="quiz-result">
        <p className="font-mono text-xs uppercase tracking-widest text-primary">Quiz complete</p>
        <p className="mt-3 font-serif text-5xl text-foreground" data-testid="quiz-score">
          {score}/{questions.length}
        </p>
        {passMark && (
          <p className={`mt-2 font-medium ${score >= passMark ? "text-emerald-500" : "text-red-500"}`} data-testid="quiz-pass-status">
            {score >= passMark ? `Passed! (pass mark ${passMark}/${questions.length})` : `Not quite — you need ${passMark}/${questions.length} to pass. Review the lesson and retry.`}
          </p>
        )}
        <p className="mt-2 text-muted-foreground">
          {pct >= 80 ? "Beautifully read! 🎼" : pct >= 50 ? "Good progress — keep practicing." : "Review the lesson and try again."}
        </p>
        <Button onClick={restart} data-testid="quiz-retry" className="mt-6 rounded-full bg-primary text-foreground hover:bg-primary/90">
          <RotateCcw size={16} className="mr-2" /> Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="quiz-engine">
      <div className="mb-4 flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-widest text-primary">
          {title || "Quiz"} · {idx + 1}/{questions.length}
        </span>
        <span className="flex items-center gap-2"><SoundToggle testId="quiz-sound-toggle" /><span className="whitespace-nowrap font-mono text-xs text-muted-foreground">Score {score}</span></span>
      </div>

      <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(idx / questions.length) * 100}%` }} />
      </div>

      <h3 className="mt-5 font-serif text-xl text-foreground" data-testid="quiz-prompt">
        {q.prompt}
      </h3>

      {q.type === "note-id" && (
        <div className="my-5 rounded-xl border border-border bg-background p-3">
          <StaffViewer clef={q.clef} notes={[{ pitch: q.pitch }]} width={360} testId={`quiz-staff-${q.id}`} />
          <button
            onClick={() => playPitch(q.pitch)}
            data-testid={`quiz-play-${q.id}`}
            className="mx-auto mt-1 flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm text-primary hover:bg-primary/20"
          >
            <Volume2 size={15} /> Hear the note
          </button>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-2">
        {q.options.map((opt) => {
          const isCorrect = opt === q.answer;
          const isChosen = opt === selected;
          let cls = "border-border bg-background text-foreground hover:border-primary";
          if (answered && isCorrect) cls = "border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
          else if (answered && isChosen && !isCorrect) cls = "border-red-500 bg-red-500/15 text-red-600 dark:text-red-400";
          else if (answered) cls = "border-border bg-background text-muted-foreground";
          return (
            <button
              key={opt}
              onClick={() => choose(opt)}
              disabled={answered}
              data-testid={`quiz-option-${opt}`}
              className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left font-medium transition-all ${cls}`}
            >
              {opt}
              {answered && isCorrect && <Check size={18} />}
              {answered && isChosen && !isCorrect && <X size={18} />}
            </button>
          );
        })}
      </div>

      {answered && (
        <Button onClick={next} data-testid="quiz-next" className="mt-6 w-full rounded-full bg-primary text-foreground hover:bg-primary/90">
          {idx + 1 >= questions.length ? "See results" : "Next question"}
        </Button>
      )}
    </div>
  );
};

export default QuizEngine;
